import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Onboarding from "./pages/Onboarding";
import Discover from "./pages/Discover";
import Matches from "./pages/Matches";
import Chat from "./pages/Chat";
import Profile from "./pages/Profile";
import ProfileDetail from "./pages/ProfileDetail";
import AboutMe from "./pages/AboutMe";
import AnswersReview from "./pages/AnswersReview";
import Premium from "./pages/Premium";
import PremiumSuccess from "./pages/PremiumSuccess";
import Admirers from "./pages/Admirers";

import Notifications from "./pages/Notifications";
import Activity from "./pages/Activity";
import Encounters from "./pages/Encounters";
import Billing from "./pages/Billing";
import Articles from "./pages/Articles";
import ArticleDetail from "./pages/ArticleDetail";
import ProfileViews from "./pages/ProfileViews";
import Terms from "./pages/Terms";
import Refunds from "./pages/Refunds";
import Privacy from "./pages/Privacy";
import Contact from "./pages/Contact";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Admin from "./pages/Admin";
import AdminUsers from "./pages/AdminUsers";
import AdminCoupons from "./pages/AdminCoupons";
import AdminArticles from "./pages/AdminArticles";
import FoundersBanner from "./components/FoundersBanner";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#0C0E12] flex items-center justify-center text-[#A29F9B]">A carregar...</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Admin user is a back-office account — never let it touch matchmaking pages.
  if (user.is_admin) return <Navigate to="/admin" replace />;
  return children;
};

const AdminOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#0C0E12] flex items-center justify-center text-[#A29F9B]">A carregar...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/discover" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user?.is_admin) return <Navigate to="/admin" replace />;
  if (user) return <Navigate to={user.onboarding_complete ? "/discover" : "/onboarding"} replace />;
  return children;
};

function AppShell() {
  const { theme } = useTheme();
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster theme={theme === "light" ? "light" : "dark"} position="top-right" richColors />
          <FoundersBanner />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route path="/discover" element={<Protected><Discover /></Protected>} />
            <Route path="/matches" element={<Protected><Matches /></Protected>} />
            <Route path="/actividade" element={<Protected><Activity /></Protected>} />
            <Route path="/encontros" element={<Protected><Encounters /></Protected>} />
            <Route path="/faturacao" element={<Protected><Billing /></Protected>} />
            <Route path="/visitantes" element={<Protected><ProfileViews /></Protected>} />
            <Route path="/artigos" element={<Articles />} />
            <Route path="/artigos/:slug" element={<ArticleDetail />} />
            <Route path="/termos" element={<Terms />} />
            <Route path="/reembolsos" element={<Refunds />} />
            <Route path="/privacidade" element={<Privacy />} />
            <Route path="/esqueci-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/contacto" element={<Contact />} />
            <Route path="/contactos" element={<Navigate to="/contacto" replace />} />
            <Route path="/chat/:matchId" element={<Protected><Chat /></Protected>} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/profile/sobre-mim" element={<Protected><AboutMe /></Protected>} />
            <Route path="/profile/respostas" element={<Protected><AnswersReview /></Protected>} />
            <Route path="/profile/:id" element={<Protected><ProfileDetail /></Protected>} />
            <Route path="/dashboard" element={<Navigate to="/actividade" replace />} />
            <Route path="/premium" element={<Protected><Premium /></Protected>} />
            <Route path="/premium/sucesso" element={<Protected><PremiumSuccess /></Protected>} />
            <Route path="/admiradores" element={<Protected><Admirers /></Protected>} />
            <Route path="/notificacoes" element={<Protected><Notifications /></Protected>} />
            <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
            <Route path="/admin/utilizadores" element={<AdminOnly><AdminUsers /></AdminOnly>} />
            <Route path="/admin/cupoes" element={<AdminOnly><AdminCoupons /></AdminOnly>} />
            <Route path="/admin/artigos" element={<AdminOnly><AdminArticles /></AdminOnly>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppShell />
    </ThemeProvider>
  );
}

export default App;
